/**
 * Domain-related utility functions for spellen.land
 * 
 * This module provides utilities for detecting the current domain and setting
 * appropriate language and country defaults based on the domain.
 * 
 * Domain Configuration:
 * - spellen.land: Dutch language (nl) + Netherlands country (NL) + EUR currency
 * - videogame.land: English language (en) + United States country (US) + USD currency
 * - localhost/other: Dutch language (nl) + Netherlands country (NL) + EUR currency (for spellen.land project)
 * 
 * The language detection works as follows:
 * 1. Check if user has a stored language preference in localStorage
 * 2. If no stored preference, use domain-based detection
 * 3. Fallback to Dutch for unknown domains (for spellen.land project)
 * 
 * This enables automatic localization based on the domain without requiring
 * users to manually select their language on first visit.
 */

export type SupportedDomain = 'spellen.land' | 'videogame.land' | 'localhost'

/**
 * Get the current domain from the window location
 * For testing on localhost, you can override the domain using:
 * - URL parameter: ?test-domain=videogame.land
 * - localStorage: set 'test-domain' to 'videogame.land'
 */
export const getCurrentDomain = (): string => {
    // Check if we're in a browser environment
    if (typeof window !== 'undefined') {
        // Check for test domain override in URL parameters
        const urlParams = new URLSearchParams(window.location.search)
        const testDomain = urlParams.get('test-domain')
        if (testDomain && (testDomain === 'spellen.land' || testDomain === 'videogame.land')) {
            return testDomain
        }

        // Check for test domain override in localStorage
        const storedTestDomain = localStorage.getItem('test-domain')
        if (storedTestDomain && (storedTestDomain === 'spellen.land' || storedTestDomain === 'videogame.land')) {
            return storedTestDomain
        }

        return window.location.hostname
    }

    // Server-side fallback - use environment variable or default
    return process.env.DOMAIN || 'spellen.land'
}

/**
 * Set a test domain for localhost testing
 * @param domain The domain to test ('spellen.land' or 'videogame.land')
 */
export const setTestDomain = (domain: 'spellen.land' | 'videogame.land'): void => {
    if (typeof window !== 'undefined') {
        localStorage.setItem('test-domain', domain)
        // Reload the page to apply the change
        window.location.reload()
    }
}

/**
 * Clear the test domain override
 */
export const clearTestDomain = (): void => {
    if (typeof window !== 'undefined') {
        localStorage.removeItem('test-domain')
        // Reload the page to apply the change
        window.location.reload()
    }
}

/**
 * Check if the current domain is spellen.land
 */
export const isSpellenLand = (): boolean => {
    return getCurrentDomain() === 'spellen.land'
}

/**
 * Get the default language based on the current domain
 * - videogame.land: English ('en')
 * - spellen.land: Dutch ('nl')
 * - localhost/other: Dutch ('nl') for spellen.land project
 */
export const getDefaultLanguageFromDomain = (): string => {
    const hostname = getCurrentDomain()

    if (hostname === 'videogame.land') {
        return 'en'
    }

    if (hostname === 'spellen.land') {
        return 'nl'
    }

    // Default fallback (for localhost, etc.) - use Dutch for spellen.land project
    return 'nl'
}

/**
 * Get the default country based on the current domain
 * - videogame.land: United States ('US')
 * - spellen.land: Netherlands ('NL')
 * - localhost/other: Netherlands ('NL') for spellen.land project
 */
export const getDefaultCountryFromDomain = (): string => {
    const hostname = getCurrentDomain()

    if (hostname === 'videogame.land') {
        return 'US'
    }

    if (hostname === 'spellen.land') {
        return 'NL'
    }

    // Default fallback (for localhost, etc.) - use NL for spellen.land project
    return 'NL'
}

/**
 * Get the expected language and country combination for the current domain
 */
export const getDomainDefaults = () => {
    return {
        language: getDefaultLanguageFromDomain(),
        country: getDefaultCountryFromDomain(),
    }
} 